import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { ValidationUtilityService } from '../../shared/utils/validation.util/validation-utility.service';
import { SpinnerComponent } from '../../../modules/shared/spinner/spinner.component';
import { ServiceRouter } from '../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../constants/service.constants';
import { Environment } from '../../../config/environment';
import { Config } from '../../../config/config';
import { VerificationBean } from '../../../beans/account/account-module.bean';
import { Subscription } from 'rxjs';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { AppUtil } from 'src/app/utils/app.util';

/** Code verification component */
@Component({
	selector: 'app-code-verification',
	templateUrl: './code-verification.component.html',
	styleUrls: ['./code-verification.component.scss'],
})
export class CodeVerificationComponent implements OnInit, OnDestroy {
	/** user email id */
	email: any;
	/** user name */
	name: any;
	/** user EPMS PID */
	epmsPID: any;
	/** UUID */
	uuid: any;
	/** Verification account form */
	verifyAccountForm: FormGroup;
	/** denote code resent */
	codeResent: any = false;
	/** */
	canLoad: boolean = false;
	/** list of subscriptions used in the component */
	subscriptions: Subscription[] = [];
	/** Analytics object */
	analyticsData: AnalyticsBean;
	/** denote if the code invalid */
	codeInvalid: boolean;
	/** Return Url */
	returnUrl: string = '';
	/**
	 * Code verification component constructor
	 * @param router - Angular Router
	 * @param serviceRouter - Service router
	 * @param spinner - Spinner
	 * @param environment - Environment Config
	 * @param config - URL Config
	 * @param activatedRoute - Angular Activated route
	 */
	constructor(
		public router: Router,
		private serviceRouter: ServiceRouter,
		private spinner: SpinnerComponent,
		private environment: Environment,
		private config: Config,
		private activatedRoute: ActivatedRoute
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'code-verification';
		this.analyticsData.pageUrl = '/registration/verification/code';

		/** Subscribing the query params and fetch info */
		this.subscriptions.push(
			this.activatedRoute.queryParams.subscribe(params => {
				try {
					this.canLoad = false;
					if (params && params.params) {
						const queryParamsObj = JSON.parse(atob(params.params));
						this.name = queryParamsObj.name;
						this.email = queryParamsObj.email;
						this.epmsPID = queryParamsObj.epmsPID;
						this.uuid = queryParamsObj.uuid;
						this.canLoad = true;
					} else {
						this.router.navigateByUrl('/');
					}
					if (params.returnUrl) {
						this.returnUrl = params.returnUrl;
					}
				} catch (Exception) {
					this.router.navigateByUrl('/');
				}
			})
		);
		this.codeInvalid = false;
	}

	/** On Init */
	ngOnInit() {
		/** Verification code form */
		this.verifyAccountForm = new FormGroup({
			verificationCode: new FormControl(
				'',
				Validators.compose([Validators.required, ValidationUtilityService.verificationCodeValidatorRegistration])
			),
		});
		this.verifyAccountForm.get('verificationCode').valueChanges.subscribe(value => {
			this.codeInvalid = false;
		});
	}

	/** Verify the code */
	verifyCode() {
		this.codeInvalid = false;
		this.analyticsData.id = 'Submit verification code';
		this.analyticsData.redirectedTo = 'verification/email';
		this.analyticsData.placement = 'Registration code verification';
		if (this.verifyAccountForm.valid) {
			const request = new VerificationBean();
			request.uuid = this.uuid;
			request.emailAddress = this.email;
			request.verificationCode = this.verifyAccountForm.getRawValue().verificationCode;
			this.spinner.startLoader();
			this.subscriptions.push(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.registrationApi.createAccount + `/${this.epmsPID}/verification`,
						METHOD_TYPE.GENERAL_POST,
						request
					)
					.subscribe(
						(response: any) => {
							this.spinner.stopLoader();
							const queryParamsObj = {
								email: this.email,
								epmsPID: this.epmsPID,
								uuid: this.uuid,
							};
							const navigationParams: NavigationExtras = {
								queryParams: {
									params: btoa(JSON.stringify(queryParamsObj)),
								},
							};
							let navigationPath = 'registration/verification/email';
							if (this.router.routerState.snapshot.url.includes('embed')) {
								navigationPath = 'registration/embed/verification/email';
							}
							if (this.returnUrl !== '') {
								navigationParams.queryParams['returnUrl'] = this.returnUrl;
							}
							this.router.navigate([navigationPath], navigationParams);
						},
						err => {
							this.spinner.stopLoader();
							this.verifyAccountForm.controls.verificationCode.setErrors({
								invalid: true,
							});
							this.codeInvalid = true;
							this.codeResent = false;
							console.error(
								`[${CodeVerificationComponent.name}][${this.verifyCode.name}]`,
								'Error occurred while sending verification code during registration flow to email : ',
								this.email,
								err
							);
						}
					)
			);
		}
	}

	/** resend Verification code */
	resendVerificationCode() {
		this.analyticsData.id = 'Resend verification code';
		this.analyticsData.redirectedTo = 'verification/code';
		this.analyticsData.placement = 'Registration code verification';
		// resetting the form
		this.codeResent = true;
		this.codeInvalid = false;
		this.verifyAccountForm.reset();
		const request = new VerificationBean();
		request.uuid = this.uuid;
		request.emailAddress = this.email;
		request.verificationCode = this.verifyAccountForm.getRawValue().verificationCode;
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount + `/${this.epmsPID}/verification/resend`,
					METHOD_TYPE.GENERAL_POST,
					request
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
					},
					err => {
						this.spinner.stopLoader();
						console.error(
							`[${CodeVerificationComponent.name}][${this.resendVerificationCode.name}]`,
							'Error occurred while resending verification code during registration flow to email : ',
							this.email,
							err
						);
					}
				)
		);
	}

	/** On destroy the component unsubscribing the subscriptions */
	ngOnDestroy() {
		// prevent memory leak when component destroyed
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
